// The outflow calendar as rows a person can change, the recurring debits the
// statements show that are not yet in it, and the settings. Every row is data in
// cash_obligation_schedule (db/migrations/0344); nothing about a supplier lives
// in code.
import { useEffect, useState } from 'react'
import { Plus } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { cn } from '../../lib/shadcn/utils'
import { Chip } from '../interviews/Chip'
import { useSaveCashPlanRow, useSaveCashPlanSettings } from '../../hooks/backend/banking'
import {
  AMOUNT_RULE_LABEL, AMOUNT_UNKNOWN, CATEGORY_LABEL, formatDay, formatDayYear, formatZar, ordinal, whenLabel,
  type Freshness, type RecurringRow, type ScheduleRow, type Settings,
} from '../../utils/cashPlan'
import type { RowDraft } from './Dialogs'

export function PlanTable({ rows, onEdit, onAdd }: { rows: ScheduleRow[]; onEdit: (r: ScheduleRow) => void; onAdd: () => void }) {
  return (
    <section className="rounded-lg border border-border bg-card">
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
        <h3 className="text-sm font-semibold">The plan</h3>
        <span className="text-xs text-muted-foreground">Every date, amount rule and supplier is a row here. Change one and the ledger follows.</span>
        <Button className="ml-auto" size="sm" variant="outline" onClick={onAdd}><Plus className="mr-1 h-4 w-4" /> Add a row</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[60rem] text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">Item</th>
              <th className="px-3 py-2 font-medium">When</th>
              <th className="px-3 py-2 font-medium">Amount rule</th>
              <th className="px-3 py-2 font-medium">Last on the statement</th>
              <th className="px-3 py-2 font-medium">Next</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className={cn('border-t border-border align-top', !r.active && 'opacity-60')}>
                <td className="px-3 py-2">
                  <div className="font-medium">{r.supplier}</div>
                  <div className="text-xs text-muted-foreground">{CATEGORY_LABEL[r.category]}{r.active ? '' : ', not in the plan'}</div>
                  {r.notes && <div className="mt-1 max-w-sm text-xs text-muted-foreground">{r.notes}</div>}
                </td>
                <td className="px-3 py-2 text-xs">{whenLabel(r)}</td>
                <td className="px-3 py-2 text-xs">
                  {AMOUNT_RULE_LABEL[r.amount_rule]}{r.amount_rule === 'fixed' ? `: ${formatZar(r.fixed_amount_zar)}` : ''}
                  <div className="text-muted-foreground">{r.source}</div>
                </td>
                <td className="px-3 py-2 text-xs whitespace-nowrap">
                  {r.last_seen ? <>{formatZar(r.last_seen.amount_zar)}<div className="text-muted-foreground">{formatDayYear(r.last_seen.date)}</div></> : r.match_pattern ? 'Not found' : 'No bank pattern'}
                </td>
                <td className="px-3 py-2 text-xs whitespace-nowrap">
                  {r.next ? <>{r.next.known ? formatZar(r.next.amount_zar) : AMOUNT_UNKNOWN}<div className="text-muted-foreground">{formatDay(r.next.date)}</div></> : r.active ? 'Not in the next eight weeks' : ''}
                </td>
                <td className="px-3 py-2 text-right"><Button size="sm" variant="ghost" onClick={() => onEdit(r)}>Change</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

export function RecurringDetected({ rows, onAdd, onSaved }: { rows: RecurringRow[]; onAdd: (d: RowDraft) => void; onSaved: () => void }) {
  const save = useSaveCashPlanRow<{ id: string }>()
  const [err, setErr] = useState<string | null>(null)
  const notPlanned = async (r: RecurringRow) => {
    setErr(null)
    try {
      // Kept as a row that is not in the plan, so the pattern stops it being
      // detected again and the decision has a date and an author.
      await save.trigger({
        supplier: r.stem, category: 'other', rule_kind: 'last_charge_day', business_day_rule: 'none', amount_rule: 'last_debit',
        match_pattern: r.suggested_pattern, source: 'Detected on the statements', active: false,
        notes: 'Marked as not a planned cost from "Recurring debits not yet in the plan".',
      })
      onSaved()
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)) }
  }
  const window = rows[0] ? `${formatDayYear(rows[0].window_from)} to ${formatDayYear(rows[0].window_to)}` : ''
  return (
    <section className="rounded-lg border border-border bg-card">
      <h3 className="border-b border-border px-4 py-2 text-sm font-semibold">Recurring debits not yet in the plan</h3>
      <p className="px-4 pt-2 text-xs text-muted-foreground">
        Debit orders and card purchases seen in three or more of the last six statement months{window ? ` (${window})` : ''} that no plan row
        recognises. Add each to the plan, or mark it as not a planned cost.
      </p>
      {err && <p className="mx-4 mt-2 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">{err}</p>}
      {rows.length === 0 ? <p className="px-4 py-4 text-sm text-muted-foreground">Every recurring debit is in the plan.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[48rem] text-sm">
            <thead className="text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">On the statement as</th>
                <th className="px-3 py-2 font-medium">Months</th>
                <th className="px-3 py-2 text-right font-medium">Last</th>
                <th className="px-3 py-2 text-right font-medium">Range</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.stem} className="border-t border-border align-top">
                  <td className="px-3 py-2">{r.stem}<div className="text-xs text-muted-foreground">{r.typical_day ? `Usually around the ${ordinal(r.typical_day)}, ` : ''}{r.occurrences} times</div></td>
                  <td className="px-3 py-2 tabular-nums">{r.months_seen} of 6</td>
                  <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{formatZar(r.last_amount_zar)}<div className="text-xs text-muted-foreground">{formatDayYear(r.last_date)}</div></td>
                  <td className="px-3 py-2 text-right text-xs tabular-nums whitespace-nowrap">{formatZar(r.min_amount_zar)} to {formatZar(r.max_amount_zar)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => onAdd({
                      supplier: r.stem.replace(/^(POS Purchase|Magtape Debit)\s+/i, ''), category: 'other',
                      rule_kind: 'last_charge_day', business_day_rule: /^Magtape/i.test(r.stem) ? 'next' : 'none',
                      amount_rule: 'last_debit', match_pattern: r.suggested_pattern, source: `FNB: ${r.stem}`, active: true,
                    })}>Add to the plan</Button>
                    <Button className="ml-1" size="sm" variant="ghost" disabled={save.loading} onClick={() => void notPlanned(r)}>Not a planned cost</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

export function FreshnessList({ rows, holidaysTo, coveredTo }: { rows: Freshness[]; holidaysTo: string | null; coveredTo: string | null }) {
  return (
    <section className="rounded-lg border border-border bg-card p-4 text-sm">
      <h3 className="mb-2 font-semibold">What the plan is reading</h3>
      <ul className="space-y-1 text-xs">
        <li>Bank statements loaded to {coveredTo ? formatDayYear(coveredTo) : 'nothing yet'}.</li>
        <li>Public holidays loaded to {holidaysTo ? formatDayYear(holidaysTo) : 'nothing'}. A date after that is moved for weekends only.</li>
        {rows.map((f) => (
          <li key={f.report_key} className="flex flex-wrap items-center gap-2">
            <span>{f.label}: loaded to {f.last_date ? formatDayYear(f.last_date) : 'nothing'}</span>
            <Chip tone={f.state === 'current' ? 'green' : 'amber'}>{f.state === 'current' ? 'Current' : `${f.days_behind ?? '?'} days behind`}</Chip>
          </li>
        ))}
      </ul>
    </section>
  )
}

export function SettingsForm({ settings, onSaved }: { settings: Settings; onSaved: () => void }) {
  const save = useSaveCashPlanSettings<Settings>()
  const [s, setS] = useState(settings)
  const [err, setErr] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  useEffect(() => { setS(settings) }, [settings])
  const field = (k: keyof Settings, label: string, hint: string, type = 'text') => (
    <div className="space-y-1">
      <Label htmlFor={`cp-s-${k}`}>{label}</Label>
      <Input id={`cp-s-${k}`} type={type} inputMode={type === 'text' ? 'decimal' : undefined} value={String(s[k] ?? '')}
        onChange={(e) => { setDone(false); setS({ ...s, [k]: e.target.value } as Settings) }} />
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  )
  const submit = async () => {
    setErr(null); setDone(false)
    try { await save.trigger({ ...s }); setDone(true); onSaved() } catch (e) { setErr(e instanceof Error ? e.message : String(e)) }
  }
  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <h3 className="mb-3 text-sm font-semibold">Settings</h3>
      <div className="grid gap-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
        {field('buffer_zar', 'Buffer in rand', 'A day below this is red.')}
        {field('horizon_days', 'Days ahead', 'How far the ledger runs, 14 to 120.')}
        {field('shifts_per_car_per_day', 'Shifts a car a day', 'Scheduled shifts are cars on the road times this times 7.')}
        {field('uber_secondary_zar', 'Second Uber credit', 'Paid the business day after the main credit.')}
        {field('uber_tolerance_pct', 'Uber tolerance, percent', 'A pay week within this agrees.')}
        {field('forecast_trailing_weeks', 'Weeks behind a forecast', 'Closed weeks averaged for payout per shift.')}
        {field('last_debit_stale_days', 'Stale after, days', 'A last debit older than this is Amount unknown.')}
        {field('variance_from', 'Running variance from', 'The first pay week in the running total.', 'date')}
        <div className="space-y-1">
          <Label htmlFor="cp-s-petrol">Petrol per shift</Label>
          <NativeSelect id="cp-s-petrol" value={s.petrol_rate_basis} onChange={(e) => { setDone(false); setS({ ...s, petrol_rate_basis: e.target.value as Settings['petrol_rate_basis'] }) }}>
            <option value="sasol_actual">Actual Sasol spend, last four statement weeks</option>
            <option value="band">Band check: budget share of earnings at the pump price</option>
          </NativeSelect>
          <p className="text-xs text-muted-foreground">Both are shown in Variance.</p>
        </div>
      </div>
      {err && <p className="mt-3 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">{err}</p>}
      <div className="mt-4 flex items-center gap-3">
        <Button variant="brand" size="sm" disabled={save.loading} onClick={() => void submit()}>Save settings</Button>
        {done && <span className="text-xs text-emerald-700 dark:text-emerald-300">Saved. The ledger has been recalculated.</span>}
      </div>
    </section>
  )
}
